/** Shared value formatting for the UI, in the active interface language. */
import { useMemo } from "react";

import { FORMAT_LOCALE, useLocaleStore, type Locale } from "../store/localeStore";

/**
 * "2026-02-04" → "04.02.2026" (ru/kk) or "02/04/2026" (en).
 *
 * `timeZone: "UTC"` is not optional. The input is a calendar date with no time
 * in it; building a Date from it lands on midnight UTC, and formatting that in
 * a negative-offset zone would render the previous day. The old hand-rolled
 * version never touched Date and so never had the bug — this keeps that
 * property while letting the locale decide the order of the parts.
 */
export function formatDate(locale: Locale, iso: string): string {
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts.map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return iso;

  return new Intl.DateTimeFormat(FORMAT_LOCALE[locale], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Integer with the locale's thousands separator. */
export function formatInt(locale: Locale, value: number): string {
  return new Intl.NumberFormat(FORMAT_LOCALE[locale]).format(Math.round(value));
}

/**
 * Formatters bound to the active locale.
 *
 * A hook rather than module functions reading the store directly: this way a
 * component that formats a number is subscribed to the language it formatted
 * it in, and cannot be left showing `4,763` after a switch to Russian.
 */
export function useFormat() {
  const locale = useLocaleStore((s) => s.locale);
  return useMemo(
    () => ({
      date: (iso: string) => formatDate(locale, iso),
      int: (value: number) => formatInt(locale, value),
    }),
    [locale],
  );
}
